import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  LeadType,
  LeadTypeTranslation
} from '@shared/interfaces/setting.interface';

// Quill leaves markup like "<p><br></p>" behind when its content is deleted,
// so a simple truthiness check can't tell an empty rich text from a real one.
export function isBlankConsentHtml(html: string): boolean {
  if (!html) {
    return true;
  }
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

@Component({
    selector: 'app-lead-gen-card',
    templateUrl: './lead-gen-card.component.html',
    styles: [':host ::ng-deep .ql-editor img { max-width: 100%; height: auto; }'],
    standalone: false
})
export class LeadGenCardComponent {
  // Data inputs
  @Input() logoUrl: string;
  @Input() logoAlt: string;
  @Input() leadTypes: LeadType[] = [];
  @Input() description: string;
  @Input() consentEnabled = false;
  @Input() consentText: string;
  @Input() defaultConsentText: string;

  // State inputs
  @Input() isSubmitted = false;
  @Input() selectedLeadType: string;
  @Input() email: string;
  @Input() isSubmitting = false;
  @Input() mode: 'form' | 'none' = 'form';

  // Text inputs (translated by parent)
  @Input() titleText: string;
  @Input() subtitleText: string;
  @Input() emailLabel: string;
  @Input() emailHelp: string;
  @Input() contactButtonText: string;
  @Input() noThanksText: string;
  @Input() noDescriptionText: string;
  @Input() resetButtonText: string;
  @Input() fixedThankYouText: string;
  @Input() defaultButtonText: string;

  // Language
  @Input() language = 'en';

  // Display flags
  @Input() showNoDescription = false;
  @Input() showResetButton = false;
  @Input() skipEmailValidation = false;

  // Outputs
  @Output() leadTypeChange = new EventEmitter<string>();
  @Output() emailChange = new EventEmitter<string>();
  @Output() submitForm = new EventEmitter<void>();
  @Output() noThanks = new EventEmitter<void>();
  @Output() resetPreview = new EventEmitter<void>();
  @Output() consentChange = new EventEmitter<boolean>();
  consentChecked = false;

  emailTouched = false;

  get selectedLeadTypeObj(): LeadType | undefined {
    return this.leadTypes.find(
      (lt) => (lt.contact_type || lt.id) === this.selectedLeadType
    );
  }

  get absoluteDescription(): string {
    if (!this.description) {
      return this.description;
    }
    return this.description.replace(
      /href="([^"]+)"/gi,
      (_, url) => `href="${this.absoluteUrl(url)}"`
    );
  }

  get isValidEmail(): boolean {
    if (!this.email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
  }

  get canSubmit(): boolean {
    const emailOk = this.skipEmailValidation || this.isValidEmail;
    const consentOk = !this.consentEnabled || this.consentChecked;
    return !!this.selectedLeadType && emailOk && consentOk && !this.isSubmitting;
  }

  get consentTextHtml(): string {
    const text = isBlankConsentHtml(this.consentText)
      ? this.defaultConsentText
      : this.consentText;
    if (!text) {
      return text;
    }
    return text
      .replace(/href="([^"]+)"/gi, (_, url) => `href="${this.absoluteUrl(url)}"`)
      .replace(/<a\b(?![^>]*\btarget=)/gi, '<a target="_blank" rel="noopener" ');
  }

  onConsentToggle(event: Event): void {
    this.consentChecked = (event.target as HTMLInputElement).checked;
    this.consentChange.emit(this.consentChecked);
  }

  getLeadTypeField(
    leadType: LeadType,
    field: keyof LeadTypeTranslation
  ): string {
    return (
      leadType.translations?.[this.language]?.[field] ||
      (leadType as any)[field] ||
      ''
    );
  }

  onLeadTypeSelect(leadType: LeadType) {
    this.leadTypeChange.emit(leadType.contact_type || leadType.id);
  }

  onEmailInput(event: Event) {
    this.emailTouched = true;
    this.emailChange.emit((event.target as HTMLInputElement).value);
  }

  absoluteUrl(url: string): string {
    if (!url) {
      return url;
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
